import clsx from 'clsx'
import { LinearGradient } from 'expo-linear-gradient'
import { Redirect, useRouter } from 'expo-router'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Platform, Pressable, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'

import { Button } from '@/src/elements/button/default'
import { Header } from '@/src/elements/header'
import { Icon } from '@/src/elements/media/Icon'
import { Modal } from '@/src/elements/modal/Modal'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import { usePrayersOnIntentionsQuery } from '@/src/features/intention/services/usePrayersOnIntentionsQuery'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { useWatchmanDailyIntentionsQuery } from '../../services/useWatchmanDailyIntentionsQuery'
import { WatchmanSummary } from '../components/watchmanSummary/WatchmanSummary'
import { WatchmanSummaryPlaceholder } from '../components/watchmanSummary/WatchmanSummaryPlaceholder'

const getBottomSizeClassNames = (hasPrayedAllIntentions: boolean) =>
  hasPrayedAllIntentions
    ? {
        height: 'h-48',
        paddingBottom: 'pb-48',
      }
    : {
        height: 'web:h-32 native:h-48',
        paddingBottom: 'pb-32',
      }

export const WatchmanIntentionsScreen = () => {
  const [showModal, setShowModal] = useState(false)
  const { t } = useTranslate()
  const { routes } = useRoutes()
  const { navigate } = useRouter()
  const router = useRouter()

  const { data: me } = useMeQuery()
  const { data, isLoading, isError } = useWatchmanDailyIntentionsQuery()

  const totalIntentions = data?.length || 0
  const prayedIntentions = data?.filter(({ status }) => status === 'prayed')
  const hasPrayedAllIntentions = prayedIntentions?.length === totalIntentions
  const sortedIntentions =
    data &&
    [...data].sort((a, b) => {
      if (a.status === b.status) return 0
      return a.status === 'prayed' ? -1 : 1
    })

  const intentionIds = (prayedIntentions || [])
    .filter((infos) => infos.intentionType === INTENTION_TYPE.INTENTION)
    .map((infos) => infos.intention.id)

  const intentionTypes = Array.from({ length: intentionIds.length }, () => INTENTION_TYPE.INTENTION)

  const { data: prayersOnIntentions } = usePrayersOnIntentionsQuery({
    intentionIds,
    intentionTypes: intentionTypes,
    enabled: intentionIds?.length > 0,
  })

  if ((!isLoading && !totalIntentions) || isError) return <Redirect href={routes.intentions()} />

  const bottomSizeClassNames = getBottomSizeClassNames(hasPrayedAllIntentions)

  const onReturn = () => router.dismissTo(routes['prayer-space']())

  return (
    <>
      <ScrollView
        testID={TEST_ID.WATCHMAN.INTENTIONS.SCREEN}
        className={clsx('max-h-screen', {
          'native:pt-safe': hasPrayedAllIntentions,
        })}
        contentContainerClassName="h-full w-full"
      >
        {!hasPrayedAllIntentions && (
          <Header
            leftButtons={[
              {
                startIcon: ICON.ARROW_LEFT,
                onPress: onReturn,
                type: 'icon',
              },
            ]}
          />
        )}
        <View className="flex flex-1 items-center justify-between native:pb-safe">
          {hasPrayedAllIntentions && (
            <View className="m-4 min-h-14 flex-row items-center justify-center">
              <Typography className="text-center" size="md" colors="default" bold>
                <FormattedMessage id="watchman.daily.intentions" />
              </Typography>
            </View>
          )}
          <View className="mx-auto flex items-center justify-center gap-2 px-6 py-8">
            <Icon
              name={ICON.LANTERN}
              size={Platform.select({ web: undefined, native: '3xl' })}
              className="h-16 w-16 text-primary-icon-contrast-lower"
            />
            <Typography heading="h1" className="text-center" size="xl" colors="primary" bold contrast="high">
              <FormattedMessage
                id={hasPrayedAllIntentions ? 'thanks.prayed.watchman.intentions' : 'watchman.daily.intentions'}
              />
            </Typography>
            {!hasPrayedAllIntentions && (
              <>
                <Pressable
                  className="flex flex-row items-center justify-center gap-1"
                  onPress={() => setShowModal(true)}
                >
                  <Typography className="text-center" size="xs" colors="default" contrast="low">
                    <FormattedMessage id="words.know-more" />
                  </Typography>
                  <Icon name={ICON.QUESTION_CIRCLE} size="xs" className="text-default-text-contrast-low" />
                </Pressable>
                <Modal
                  showModal={showModal}
                  testID={TEST_ID.PUBLICATION.SHARE.MODAL}
                  title={t('watchman.intentions.choice')}
                  description={t('watchman.intentions.explanation')}
                  showCancelButton
                  cancelButtonProps={{ className: 'mx-auto', colors: 'primary' }}
                  cancelText={t('word.close')}
                  onCancel={() => setShowModal(false)}
                >
                  <Typography className="text-start pt-2" size="md" colors="default" contrast="default">
                    <FormattedMessage id="watchman.intentions.explanation.end" />
                  </Typography>
                </Modal>
              </>
            )}
          </View>
          <View className={clsx('w-full max-w-[500px] gap-2 px-2', bottomSizeClassNames.paddingBottom)}>
            {sortedIntentions?.map((infos, index) => {
              const prayerId = prayersOnIntentions?.prayers
                ? prayersOnIntentions.prayers.find((prayer) => prayer.toIntention?.id === infos.intention.id)?.id
                : undefined
              const myUplift =
                'uplifts' in infos.intention
                  ? infos.intention.uplifts.find((uplift) => uplift.user.id === me?.id)
                  : undefined
              return (
                <WatchmanSummary
                  prayerId={prayerId}
                  key={infos.intention.id}
                  checkAnimationDelay={300 + index * 150}
                  userName={infos.intention.user?.name || t('intention.anonymous-praying')}
                  intention={'content' in infos.intention ? infos.intention.content : undefined}
                  isPrayed={infos.status == 'prayed'}
                  type={infos.intentionType}
                  uplift={myUplift}
                  birthdayDate={
                    infos.intention.user && 'birthdate' in infos.intention.user
                      ? infos.intention.user.birthdate
                      : undefined
                  }
                  id={infos.id}
                  intentionId={infos.intention.id}
                />
              )
            })}
            {!!isLoading &&
              Array.from({ length: 3 }, (_, index) => <WatchmanSummaryPlaceholder key={`placeholder-${index}`} />)}
          </View>
        </View>
      </ScrollView>
      <View
        className={clsx(
          'pointer-events-none absolute bottom-0 left-0 right-0 native:pb-safe web:bg-white',
          bottomSizeClassNames.height,
        )}
      >
        <LinearGradient locations={[0, 1]} colors={['rgba(255,255,255,0)', 'rgba(255,255,255,1)']} className="h-32" />
        <View className="h-32 w-full bg-white" />
      </View>
      <View className="absolute web:bottom-6 w-0 gap-4 self-center native:pb-safe native:bottom-0">
        {hasPrayedAllIntentions && (
          <View className="w-screen max-w-[500px] -translate-x-1/2 px-2 sm:w-auto">
            <Button
              colors="primary"
              variant="faded"
              textClassName="!text-nowrap"
              className="w-full sm:w-auto"
              fullWidth={Platform.OS !== 'web'}
              onPress={() => navigate(routes.intentions())}
            >
              <FormattedMessage id="watchman.daily.pray-for-more" />
            </Button>
          </View>
        )}
        <View className="w-screen max-w-[500px] -translate-x-1/2 px-2 sm:w-auto">
          <Button
            colors="primary"
            variant="solid"
            className="w-full sm:w-auto"
            textClassName="!text-nowrap"
            fullWidth={Platform.OS !== 'web'}
            onPress={() => (hasPrayedAllIntentions ? onReturn() : navigate(routes.watchmanIntentionsPray()))}
          >
            <FormattedMessage id={hasPrayedAllIntentions ? 'word.finish' : 'prayer.start'} />
          </Button>
        </View>
      </View>
    </>
  )
}
